import { useMemo, useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { useSetAtom } from "jotai";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Badge, Form, InputGroup, Button as BsButton } from "react-bootstrap";
import {
    Search,
    PencilFill,
    PersonFill,
    Trash,
    ArrowCounterclockwise,
    Calendar3,
    FolderPlus,
    Folder2Open,
    FolderMinus,
    FileEarmarkText,
    PersonPlus,
    People,
    Filter,
    Check2,
    SortDown,
    SortUp,
} from "react-bootstrap-icons";
import { Instant, LocalDate, ZoneOffset } from "@js-joda/core";
import { toast } from "react-toastify";
import { ActionDropdown, ActionItem } from "./Controls/ActionDropdown";
import { Button } from "./Controls/Button";
import { GroupParkerAddModal, PendingGroupParker } from "./GroupParkerAddModal";
import { GroupParkerAddSpotsModal, createExtraSpotIds } from "./GroupParkerAddSpotsModal";
import { GroupParkerFinalizeModal } from "./GroupParkerFinalizeModal";
import { ChangeParkerGroupModal } from "./ChangeParkerGroupModal";
import { GroupModal } from "./GroupModal";
import ContractDetailsModal from "./ContractDetailsModal";
import {
    ContractRuleForm,
    ContractRuleModal,
    RuleType,
    resolvePlanBasePriceCents,
} from "./ContractRuleModal";
import { UnsavedChangesModal } from "./UnsavedChangesModal";
import { useUnsavedChangesGuard } from "./PendingChangesContext";
import { cents, showMoney } from "./Data/Money";
import { getInitials, avatarColor } from "./Data/Avatar";
import { SubscriptionParker, Subscription } from "./Data/ApiTypes";
import { showDate } from "./Data/Date";
import { Api } from "./Data/Api";
import { parseSubscriptionParker } from "./Data/ApiParse";
import { createApiService } from "./shared/apiConfig";
import {
    ContractsRuleApi,
    SubscriptionGroupsApi,
    ServerRequestsSubscriptionGroupsResponse,
} from "./shared/api/axios-client";
import { groupParkerCartBannerAtom, groupParkerPendingCheckoutAtom } from "./Data/Atoms";
export { UNASSIGNED_GROUP_ID } from "./GroupParkerConstants";
import "./GroupParkerListSection.scss";

const subGroupsApi = createApiService(SubscriptionGroupsApi);
const contractsApi = createApiService(ContractsRuleApi);

type GroupFilterKey = "unassigned" | number;
type SortKey = "name" | "status" | "startDate" | "recentlyAdded";

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
    { key: "name", label: "Name" },
    { key: "status", label: "Status" },
    { key: "startDate", label: "Start Date" },
    { key: "recentlyAdded", label: "Recently Added" },
];

/** Approx card height; measureElement corrects per row. */
const ROW_ESTIMATE_PX = 72;
/** Bootstrap gap-2 */
const ROW_GAP_PX = 8;

/**
 * Monitor facility shells (AppFacility / AppFacilityParker) scroll inside
 * `.scroll-y-auto.flex-fill`, not `#content`. Connect has no such nest — there
 * `#content` is the real scroller. Prefer the nested pane when present so the
 * virtualizer updates on scroll (otherwise only the first ~viewport rows mount).
 */
function getPageScrollElement(): HTMLElement | null {
    const nested = document.querySelector<HTMLElement>(
        "#content .scroll-y-auto.flex-fill"
    );
    if (nested) return nested;
    return document.getElementById("content");
}

// ── Types ─────────────────────────────────────────────────────────────────

type ParkerStatus = "active" | "upcoming" | "canceling" | "placeholder" | "pending";

const STATUS_SORT_RANK: Record<ParkerStatus, number> = {
    active: 0,
    upcoming: 1,
    canceling: 2,
    pending: 3,
    placeholder: 4,
};

type PlaceholderKind = "base" | "extra";

interface ParkerCardData {
    key: string;
    type: "parker" | "placeholder" | "pending";
    sp?: SubscriptionParker;
    pending?: PendingGroupParker;
    status: ParkerStatus;
    label: string;
    email?: string;
    startDate?: string;
    cancelDate?: string;
    previewPrice?: number;
    previewSubtotal?: number;
    placeholderKind?: PlaceholderKind;
    extraSpotId?: string;
}

function cardStartEpoch(c: ParkerCardData): number {
    if (c.type === "parker" && c.sp?.startDate) {
        try {
            const i = typeof c.sp.startDate === "string"
                ? Instant.parse(c.sp.startDate)
                : c.sp.startDate as Instant;
            return i.toEpochMilli();
        } catch {
            return 0;
        }
    }
    if (c.type === "pending" && c.pending?.startDate) {
        try {
            return LocalDate.parse(c.pending.startDate)
                .atStartOfDay(ZoneOffset.UTC)
                .toInstant()
                .toEpochMilli();
        } catch {
            return 0;
        }
    }
    return 0;
}

function cardAddedRank(c: ParkerCardData): number {
    if (c.type === "parker" && c.sp) return c.sp.parker.parkerId ?? 0;
    if (c.type === "pending") return Number.MAX_SAFE_INTEGER;
    return -1;
}

function compareCards(a: ParkerCardData, b: ParkerCardData, sortKey: SortKey, sortAsc: boolean): number {
    // Pending always sink to bottom; open spots just above pending.
    const typeRank = (c: ParkerCardData) =>
        c.type === "pending" ? 2 : c.type === "placeholder" ? 1 : 0;
    const tr = typeRank(a) - typeRank(b);
    if (tr !== 0) return tr;
    if (a.type === "pending" || b.type === "pending") return 0;

    let cmp = 0;
    switch (sortKey) {
        case "status":
            cmp = STATUS_SORT_RANK[a.status] - STATUS_SORT_RANK[b.status];
            if (cmp === 0) cmp = a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
            break;
        case "startDate":
            cmp = cardStartEpoch(a) - cardStartEpoch(b);
            if (cmp === 0) cmp = a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
            break;
        case "recentlyAdded":
            cmp = cardAddedRank(a) - cardAddedRank(b);
            break;
        default:
            cmp = a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
            break;
    }
    return sortAsc ? cmp : -cmp;
}

function cardMatchesGroupFilters(c: ParkerCardData, selected: Set<GroupFilterKey>): boolean {
    if (selected.size === 0) return true;
    if (c.type === "placeholder") return selected.has("unassigned");
    const gid = c.type === "parker"
        ? (c.sp?.subscriptionGroupId ?? null)
        : (c.pending?.subscriptionGroupId ?? null);
    if (gid == null) return selected.has("unassigned");
    return selected.has(gid);
}

// ── Helpers ───────────────────────────────────────────────────────────────

function parkerStatus(sp: SubscriptionParker, today: LocalDate): ParkerStatus {
    if (sp.removedWhen) return "canceling";
    if (!sp.startDate) return "active";
    try {
        const instant = typeof sp.startDate === "string"
            ? Instant.parse(sp.startDate)
            : sp.startDate as Instant;
        const startDate = instant.atZone(ZoneOffset.UTC).toLocalDate();
        return startDate.isAfter(today) ? "upcoming" : "active";
    } catch {
        return "active";
    }
}

/** Active, upcoming, and canceling parkers consume quantity slots until removal is effective. */
function countAssignedSlots(
    parkers: SubscriptionParker[],
    pendingCount: number,
    today: LocalDate,
): number {
    const parkerSlots = parkers.filter(sp => {
        const status = parkerStatus(sp, today);
        return status === "active" || status === "upcoming" || status === "canceling";
    }).length;
    return parkerSlots + pendingCount;
}

function showInstantDate(instant: Instant | null | undefined): string | undefined {
    if (!instant) return undefined;
    try {
        const i = typeof instant === "string" ? Instant.parse(instant as string) : instant as Instant;
        const d = i.atZone(ZoneOffset.UTC).toLocalDate();
        return showDate(d);
    } catch {
        return undefined;
    }
}

function instantToLocalDate(instant: Instant | null | undefined): LocalDate | undefined {
    if (!instant) return undefined;
    try {
        const i = typeof instant === "string" ? Instant.parse(instant as string) : instant as Instant;
        return i.atZone(ZoneOffset.UTC).toLocalDate();
    } catch {
        return undefined;
    }
}

/** Future starts only — hide past and today. */
function formatStartsOn(start: LocalDate | undefined, today: LocalDate): string | undefined {
    if (!start || !start.isAfter(today)) return undefined;
    return `Starts ${showDate(start)}`;
}

function normName(s: string): string {
    return s.trim().toLowerCase();
}

function normEmail(s: string): string {
    return s.trim().toLowerCase();
}

function normPhone(s: string): string {
    return s.replace(/\D/g, "");
}

/** True if name, email, or phone matches any existing or pending parker. */
function isDuplicateParker(
    candidate: { name: string; emailAddress: string; phoneNumber: string },
    existing: SubscriptionParker[],
    pending: PendingGroupParker[],
): boolean {
    const candName = normName(candidate.name);
    const candEmail = normEmail(candidate.emailAddress);
    const candPhone = normPhone(candidate.phoneNumber);

    const matches = (name: string, email: string, phone: string | null | undefined) => {
        if (candName && normName(name) === candName) return true;
        const otherEmail = normEmail(email);
        if (candEmail && otherEmail && candEmail === otherEmail) return true;
        const otherPhone = normPhone(phone ?? "");
        if (candPhone && otherPhone && candPhone === otherPhone) return true;
        return false;
    };

    for (const sp of existing) {
        const email = sp.parker.isAutoGeneratedEmail ? "" : (sp.parker.emailAddress ?? "");
        if (matches(sp.parker.name, email, sp.parker.phoneNumber)) return true;
    }
    for (const p of pending) {
        if (matches(p.name, p.emailAddress, p.phoneNumber)) return true;
    }
    return false;
}

const STATUS_LABELS: Record<ParkerStatus, string> = {
    active:      "Active",
    upcoming:    "Future",
    canceling:   "Ending",
    placeholder: "Open Spot",
    pending:     "Pending",
};

/** Soft pastel badge classes matching list card design. */
const STATUS_BADGE_CLASS: Record<ParkerStatus, string> = {
    active:      "bg-parker-active",
    upcoming:    "bg-parker-upcoming",
    canceling:   "bg-parker-canceling",
    placeholder: "bg-parker-placeholder",
    pending:     "bg-parker-pending",
};

function statusBadgeProps(status: ParkerStatus, selected = true) {
    if (!selected) {
        return {
            bg: "light" as const,
            text: "dark" as const,
            className: "border",
        };
    }
    return {
        bg: undefined as undefined,
        text: undefined as undefined,
        className: STATUS_BADGE_CLASS[status],
    };
}

// ── Props ─────────────────────────────────────────────────────────────────

export interface GroupParkerListSectionProps {
    facilityId: number;
    sub: Subscription;
    parkers: SubscriptionParker[];
    setParkers: (parkers: SubscriptionParker[]) => void;
    paymentMethodLabel: string | null;
    canChargeSubscription: boolean;
    maxCreditCardAmountInCents?: number | null;
    paymentMethodIsAch?: boolean;
    sectionRef?: React.RefObject<HTMLDivElement>;
    planIsActive?: boolean;
    /** NeedsSetup: assign parkers with no preview/cart/finalize checkout. */
    isNeedsSetup?: boolean;
    onManageParker?: (sp: SubscriptionParker) => void;
    onRemoveParker?: (sp: SubscriptionParker) => void;
    onUndoCancelParker?: (sp: SubscriptionParker) => void;
    onRemoveBaseSlot?: () => void;
    onRefreshParkers?: () => Promise<SubscriptionParker[]>;
    /** Enhanced groups only */
    isEnhancedGroup?: boolean;
    groups?: ServerRequestsSubscriptionGroupsResponse[];
    onGroupsChange?: (groups: ServerRequestsSubscriptionGroupsResponse[]) => void;
    onRefreshSubscription?: () => void;
}

// ── Main component ────────────────────────────────────────────────────────

export function GroupParkerListSection({
    facilityId,
    sub,
    parkers,
    setParkers,
    paymentMethodLabel,
    canChargeSubscription,
    maxCreditCardAmountInCents = null,
    paymentMethodIsAch,
    sectionRef,
    planIsActive = false,
    isNeedsSetup = false,
    onManageParker,
    onRemoveParker,
    onUndoCancelParker,
    onRemoveBaseSlot,
    onRefreshParkers,
    isEnhancedGroup = false,
    groups = [],
    onGroupsChange,
    onRefreshSubscription,
}: GroupParkerListSectionProps) {
    const today = LocalDate.now();
    const setCartBanner = useSetAtom(groupParkerCartBannerAtom);
    const setPendingCheckout = useSetAtom(groupParkerPendingCheckoutAtom);
    /** Manage parkers/groups UI during Active or NeedsSetup. */
    const canManageParkers = planIsActive || isNeedsSetup;

    const [extraSpotIds, setExtraSpotIds] = useState<string[]>([]);
    const [pendingParkers, setPendingParkers] = useState<PendingGroupParker[]>([]);

    const [showAddSpotsModal, setShowAddSpotsModal] = useState(false);
    const [assignAfterSpotsClose, setAssignAfterSpotsClose] = useState<string | undefined>();
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [assignExtraSpotId, setAssignExtraSpotId] = useState<string | undefined>();
    const [assignFillsExtraSlot, setAssignFillsExtraSlot] = useState(false);
    const [showFinalizeModal, setShowFinalizeModal] = useState(false);

    const [changeGroupParker, setChangeGroupParker] = useState<SubscriptionParker | undefined>();
    const [removeFromGroupParker, setRemoveFromGroupParker] = useState<SubscriptionParker | undefined>();
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [showViewContracts, setShowViewContracts] = useState(false);
    const [viewContractsInitialId, setViewContractsInitialId] = useState<number | null>(null);
    const [showAddContract, setShowAddContract] = useState(false);
    const [editingGroupId, setEditingGroupId] = useState<number | undefined>();
    const [editingRules, setEditingRules] = useState<ContractRuleForm[]>([]);

    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState<ParkerStatus | "all">("all");
    const [groupFilters, setGroupFilters] = useState<Set<GroupFilterKey>>(() => new Set());
    const [sortKey, setSortKey] = useState<SortKey>("name");
    const [sortAsc, setSortAsc] = useState(true);
    const [showSortMenu, setShowSortMenu] = useState(false);
    const sortMenuRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const lastRowRef = useRef<HTMLDivElement | null>(null);
    const [scrollMargin, setScrollMargin] = useState(0);

    const groupOptions = useMemo(
        () => groups
            .filter((g): g is ServerRequestsSubscriptionGroupsResponse & { id: number; name: string } =>
                g.id != null && !!g.name)
            .map(g => ({
                id: g.id,
                name: g.name,
                contractRules: g.contractRules ?? [],
            })),
        [groups],
    );
    const [showScrollHint, setShowScrollHint] = useState(false);

    const assignedSlotCount = useMemo(
        () => countAssignedSlots(parkers, pendingParkers.length, today),
        [parkers, pendingParkers.length, today],
    );
    const basePlaceholderCount = Math.max(0, sub.quantity - assignedSlotCount);
    const totalPlaceholders = basePlaceholderCount + extraSpotIds.length;
    /** Parkers + pending + open spots currently on the plan. */
    const totalPlanSlots = assignedSlotCount + totalPlaceholders;
    const remainingSpotCap = Math.max(0, 300 - totalPlanSlots);
    /** Keep at least one parker or open spot on the plan. */
    const canRemoveSlot = totalPlanSlots > 1;

    const cartTotal = useMemo(
        () => pendingParkers.reduce((sum, p) => sum + (p.previewSubtotal ?? p.previewPrice ?? 0), 0),
        [pendingParkers]
    );

    const allCards: ParkerCardData[] = useMemo(() => {
        const cards: ParkerCardData[] = parkers.map(sp => ({
            key:        `p-${sp.parker.parkerId}`,
            type:       "parker" as const,
            sp,
            status:     parkerStatus(sp, today),
            label:      sp.parker.name || sp.parker.emailAddress,
            email:      sp.parker.isAutoGeneratedEmail ? undefined : sp.parker.emailAddress,
            startDate:  showInstantDate(sp.startDate ?? undefined),
            cancelDate: showInstantDate(sp.removedWhen ?? undefined),
        }));

        for (const p of pendingParkers) {
            cards.push({
                key:          p.key,
                type:         "pending",
                pending:      p,
                status:       "pending",
                label:        p.name,
                email:        p.emailAddress || undefined,
                startDate:    showDate(LocalDate.parse(p.startDate)),
                previewPrice: p.previewPrice,
                previewSubtotal: p.previewSubtotal ?? p.previewPrice,
            });
        }

        for (let i = 0; i < basePlaceholderCount; i++) {
            cards.push({
                key:             `base-ph-${i}`,
                type:            "placeholder",
                status:          "placeholder",
                label:           "Open Spot",
                placeholderKind: "base",
            });
        }

        for (const extraSpotId of extraSpotIds) {
            cards.push({
                key:             extraSpotId,
                type:            "placeholder",
                status:          "placeholder",
                label:           "Open Spot",
                placeholderKind: "extra",
                extraSpotId,
            });
        }

        return cards;
    }, [parkers, pendingParkers, basePlaceholderCount, extraSpotIds, today]);

    const filteredCards = useMemo(() => {
        const filtered = allCards.filter(c => {
            if (statusFilter !== "all" && c.status !== statusFilter) return false;
            if (isEnhancedGroup && !cardMatchesGroupFilters(c, groupFilters)) return false;
            if (search.trim()) {
                const q = search.toLowerCase();
                if (!c.label.toLowerCase().includes(q) && !(c.email ?? "").toLowerCase().includes(q))
                    return false;
            }
            return true;
        });
        return filtered.sort((a, b) => compareCards(a, b, sortKey, sortAsc));
    }, [allCards, statusFilter, groupFilters, isEnhancedGroup, search, sortKey, sortAsc]);

    const filtersActive = statusFilter !== "all" || groupFilters.size > 0 || search.trim().length > 0;

    const clearFilters = () => {
        setSearch("");
        setStatusFilter("all");
        setGroupFilters(new Set());
    };

    const toggleGroupFilter = (key: GroupFilterKey) => {
        setGroupFilters(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    useEffect(() => {
        if (!showSortMenu) return;
        const onDoc = (e: MouseEvent) => {
            if (sortMenuRef.current && !sortMenuRef.current.contains(e.target as Node)) {
                setShowSortMenu(false);
            }
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [showSortMenu]);

    // Page scroll is #content (not window). Virtualize against that — no nested overflow.
    useLayoutEffect(() => {
        const listEl = listRef.current;
        const scrollEl = getPageScrollElement();
        if (!listEl || !scrollEl) return;

        const updateMargin = () => {
            const scrollRect = scrollEl.getBoundingClientRect();
            const listRect = listEl.getBoundingClientRect();
            const next = listRect.top - scrollRect.top + scrollEl.scrollTop;
            setScrollMargin(prev => (Math.abs(prev - next) < 0.5 ? prev : next));
        };

        updateMargin();
        const ro = new ResizeObserver(updateMargin);
        ro.observe(scrollEl);
        if (listEl.parentElement) ro.observe(listEl.parentElement);
        return () => ro.disconnect();
    }, [parkers.length, pendingParkers.length, extraSpotIds.length, filteredCards.length]);

    const rowVirtualizer = useVirtualizer({
        count: filteredCards.length,
        getScrollElement: getPageScrollElement,
        estimateSize: () => ROW_ESTIMATE_PX,
        overscan: 8,
        gap: ROW_GAP_PX,
        scrollMargin,
    });

    const listTotalSize = rowVirtualizer.getTotalSize();

    // Fade while last parker is still below the fold. Hide at true scroll end so the
    // sticky overlay never sits on the last card when scrollTop is already maxed.
    useLayoutEffect(() => {
        const scrollEl = getPageScrollElement();
        if (!scrollEl || filteredCards.length === 0) {
            setShowScrollHint(false);
            return;
        }

        const SCROLL_END_EPS = 2;
        const CLIP_EPS = 8;

        const update = () => {
            const remaining =
                scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight;
            const atScrollEnd = remaining <= SCROLL_END_EPS;

            const lastRowEl = lastRowRef.current;
            let lastClipped: boolean;
            if (!lastRowEl) {
                const items = rowVirtualizer.getVirtualItems();
                const maxMounted = items.length ? items[items.length - 1]!.index : -1;
                lastClipped = maxMounted < filteredCards.length - 1;
            } else {
                const viewBottom = scrollEl.getBoundingClientRect().bottom;
                const lastBottom = lastRowEl.getBoundingClientRect().bottom;
                lastClipped = lastBottom > viewBottom + CLIP_EPS;
            }

            setShowScrollHint(lastClipped && !atScrollEnd);
        };

        update();
        // Remeasure after paint / measureElement settles
        let raf2 = 0;
        const raf1 = requestAnimationFrame(() => {
            update();
            raf2 = requestAnimationFrame(update);
        });

        scrollEl.addEventListener("scroll", update, { passive: true });
        window.addEventListener("resize", update);
        const ro = new ResizeObserver(update);
        ro.observe(scrollEl);
        if (listRef.current) ro.observe(listRef.current);
        if (lastRowRef.current) ro.observe(lastRowRef.current);

        return () => {
            cancelAnimationFrame(raf1);
            cancelAnimationFrame(raf2);
            scrollEl.removeEventListener("scroll", update);
            window.removeEventListener("resize", update);
            ro.disconnect();
        };
    }, [filteredCards.length, listTotalSize, scrollMargin]);

    const presentStatuses = useMemo(
        () => Array.from(new Set(allCards.map(c => c.status))) as ParkerStatus[],
        [allCards]
    );

    const statusCounts = useMemo(() => {
        const counts: Partial<Record<ParkerStatus | "all", number>> = { all: allCards.length };
        for (const c of allCards) {
            counts[c.status] = (counts[c.status] ?? 0) + 1;
        }
        return counts;
    }, [allCards]);

    const groupCounts = useMemo(() => {
        if (!isEnhancedGroup) return { all: 0, unassigned: 0 } as Record<string | number, number>;
        const counts: Record<string | number, number> = { all: 0, unassigned: 0 };
        for (const g of groups) {
            if (g.id != null) counts[g.id] = 0;
        }
        for (const c of allCards) {
            if (c.type === "placeholder") continue;
            counts.all = (counts.all ?? 0) + 1;
            const gid = c.type === "parker"
                ? (c.sp?.subscriptionGroupId ?? null)
                : (c.pending?.subscriptionGroupId ?? null);
            if (gid == null) {
                counts.unassigned = (counts.unassigned ?? 0) + 1;
            } else {
                counts[gid] = (counts[gid] ?? 0) + 1;
            }
        }
        return counts;
    }, [allCards, groups, isEnhancedGroup]);

    const applyLocalGroupChange = (
        parkerId: number,
        oldGroupId: number | null | undefined,
        groupId: number | null,
        groupName: string,
    ) => {
        setParkers(
            parkers.map(p =>
                p.parker.parkerId === parkerId
                    ? {
                        ...p,
                        subscriptionGroupId: groupId ?? undefined,
                        subscriptionGroupName: groupName,
                    }
                    : p
            )
        );
        if (onGroupsChange) {
            onGroupsChange(groups.map(g => {
                let count = g.parkerCount ?? 0;
                if (g.id === oldGroupId) count = Math.max(0, count - 1);
                if (g.id === groupId) count = count + 1;
                return { ...g, parkerCount: count };
            }));
        }
        onRefreshSubscription?.();
    };

    const handleChangeParkerGroup = async (
        sp: SubscriptionParker,
        groupId: number | null,
    ) => {
        const parkerId = sp.parker.parkerId;
        const oldGroupId = sp.subscriptionGroupId ?? null;
        try {
            await subGroupsApi.changeParkerGroup({
                serverRequestsParkerGroupChangeRequest: {
                    parkerId,
                    subscriptionId: sub.subscriptionId,
                    subscriptionGroupId: groupId,
                },
            });
            const groupName = groupId != null
                ? (groups.find(g => g.id === groupId)?.name ?? "")
                : "";
            applyLocalGroupChange(parkerId, oldGroupId, groupId, groupName);
            toast.success(groupId != null ? "Parker group updated." : "Parker removed from group.");
        } catch {
            toast.error("Could not update parker group.");
        }
    };

    const handleRemoveFromGroup = async (sp: SubscriptionParker) => {
        if (!sp.subscriptionGroupId) return;
        const oldGroupId = sp.subscriptionGroupId;
        try {
            await subGroupsApi.changeParkerGroup({
                serverRequestsParkerGroupChangeRequest: {
                    parkerId: sp.parker.parkerId,
                    subscriptionId: sub.subscriptionId,
                    subscriptionGroupId: null,
                },
            });
            applyLocalGroupChange(sp.parker.parkerId, oldGroupId, null, "");
            toast.success("Parker removed from group.");
        } catch {
            toast.error("Could not remove parker from group.");
        }
    };

    const handleCreateGroup = async (name: string) => {
        const res = await subGroupsApi.addGroup({
            serverRequestsSubscriptionGroupAddRequest: {
                subscriptionId: sub.subscriptionId,
                name,
            },
        });
        const group = res.data;
        if (group?.error) {
            toast.error(group.error);
            return undefined;
        }
        const next: ServerRequestsSubscriptionGroupsResponse = {
            id: group.id,
            name: group.name,
            parkerCount: 0,
            contractRules: [],
        };
        onGroupsChange?.([...groups, next]);
        toast.success("Group created successfully.");
        return next;
    };

    const openAddContract = (groupId: number) => {
        setEditingGroupId(groupId);
        setEditingRules([]);
        setShowAddContract(true);
    };

    const openEditContract = (g: ServerRequestsSubscriptionGroupsResponse) => {
        const rulesToEdit = (g.contractRules ?? []).map((r) => ({
            min: r.min!,
            max: r.max!,
            price: r.price!,
            noCost: r.price == null || r.price === 0,
            type: (r.price ?? 0) > 0 ? RuleType.Fee : RuleType.Discount,
        }));
        setEditingRules(rulesToEdit);
        setEditingGroupId(g.id);
        setShowAddContract(true);
    };

    const saveContract = async (data: { groupId: number; rules: ContractRuleForm[] }) => {
        const cleanRules = (data.rules as any[]).map(
            ({ _priceText, _isPriceFocused, id, ...rule }) => rule
        );
        const res = await contractsApi.contractsRulePost({
            serverRequestsContractsRuleSaveRequest: {
                subscriptionGroupId: data.groupId,
                rules: cleanRules,
            },
        });
        const result = res.data;
        if (result?.error) {
            toast.error(result.error);
            return;
        }
        onGroupsChange?.(
            groups.map((g) =>
                g.id === data.groupId
                    ? { ...g, contractRules: result.rules ?? [] }
                    : g
            )
        );
        setShowAddContract(false);
        setEditingGroupId(undefined);
        setEditingRules([]);
        toast.success("Contract saved successfully.");
        onRefreshSubscription?.();
    };

    const removeContract = async (groupId: number) => {
        try {
            const res = await contractsApi.contractsRuleSubscriptionGroupIdDelete({
                subscriptionGroupId: groupId,
            });
            if (res.data?.error) {
                toast.error(res.data.error);
                return;
            }
            onGroupsChange?.(
                groups.map((g) =>
                    g.id === groupId
                        ? { ...g, contractRules: [], hasContract: false }
                        : g
                )
            );
            setShowAddContract(false);
            setEditingGroupId(undefined);
            setEditingRules([]);
            toast.success("Contract removed successfully.");
            onRefreshSubscription?.();
        } catch {
            toast.error("Failed to remove contract.");
        }
    };

    const canAssignAnother = useMemo(() => {
        if (assignFillsExtraSlot) {
            const otherExtras = assignExtraSpotId
                ? extraSpotIds.filter(id => id !== assignExtraSpotId).length
                : Math.max(0, extraSpotIds.length - 1);
            return basePlaceholderCount > 0 || otherExtras > 0;
        }
        return basePlaceholderCount > 1 || extraSpotIds.length > 0;
    }, [assignFillsExtraSlot, assignExtraSpotId, extraSpotIds, basePlaceholderCount]);

    const handleAddToCart = async (
        pending: PendingGroupParker,
        options?: { assignAnother?: boolean },
    ) => {
        if (isDuplicateParker(pending, parkers, pendingParkers)) {
            toast.error("This parker appears to already exist.");
            throw new Error("duplicate parker");
        }

        if (isNeedsSetup) {
            const result = await Api.groupParkerFinalize(facilityId, sub.subscriptionId, {
                parkers: [{
                    name: pending.name,
                    emailAddress: pending.emailAddress,
                    phoneNumber: pending.phoneNumber || null,
                    startDate: pending.startDate,
                    subscriptionGroupId: pending.subscriptionGroupId ?? null,
                    fees: pending.fees?.map(f => ({
                        description: f.description,
                        amountCents: f.amountCents,
                        cadence: f.cadence,
                    })),
                    pendingIntegrationCredential: pending.pendingIntegrationCredential ?? null,
                }],
            });
            if (!result.isOk) {
                toast.error("Could not add parker.");
                throw new Error("groupParkerFinalize failed");
            }
            const created = (result.value.parkers ?? []).map(parseSubscriptionParker);
            if (pending.isExtraSlot) {
                if (assignExtraSpotId) {
                    setExtraSpotIds(ids => ids.filter(id => id !== assignExtraSpotId));
                    setAssignExtraSpotId(undefined);
                } else {
                    setExtraSpotIds(ids => ids.slice(1));
                }
            }
            let updatedParkers = parkers;
            if (onRefreshParkers) {
                updatedParkers = await onRefreshParkers();
            } else if (created.length > 0) {
                updatedParkers = [...parkers, ...created];
                setParkers(updatedParkers);
            }
            toast.success("Parker added.");
            onRefreshSubscription?.();

            if (options?.assignAnother) {
                const assigned = countAssignedSlots(updatedParkers, 0, today);
                const baseCount = Math.max(0, sub.quantity - assigned);
                const extras = pending.isExtraSlot
                    ? (assignExtraSpotId
                        ? extraSpotIds.filter(id => id !== assignExtraSpotId)
                        : extraSpotIds.slice(1))
                    : extraSpotIds;
                if (baseCount > 0) {
                    setAssignFillsExtraSlot(false);
                    setAssignExtraSpotId(undefined);
                } else if (extras.length > 0) {
                    setAssignFillsExtraSlot(true);
                    setAssignExtraSpotId(extras[0]);
                } else {
                    setShowAssignModal(false);
                }
                return;
            }
            setShowAssignModal(false);
            return;
        }

        const nextPendingCount = pendingParkers.length + 1;
        let nextExtraIds = extraSpotIds;

        setPendingParkers(prev => [...prev, pending]);
        if (pending.isExtraSlot) {
            if (assignExtraSpotId) {
                nextExtraIds = extraSpotIds.filter(id => id !== assignExtraSpotId);
                setExtraSpotIds(nextExtraIds);
            } else {
                nextExtraIds = extraSpotIds.slice(1);
                setExtraSpotIds(nextExtraIds);
            }
            setAssignExtraSpotId(undefined);
        }

        if (options?.assignAnother) {
            const assigned = countAssignedSlots(parkers, nextPendingCount, today);
            const baseCount = Math.max(0, sub.quantity - assigned);
            if (baseCount > 0) {
                setAssignFillsExtraSlot(false);
                setAssignExtraSpotId(undefined);
            } else if (nextExtraIds.length > 0) {
                setAssignFillsExtraSlot(true);
                setAssignExtraSpotId(nextExtraIds[0]);
            } else {
                setShowAssignModal(false);
            }
            return;
        }

        setShowAssignModal(false);
    };

    const handleRemovePending = (key: string) => {
        const pending = pendingParkers.find(p => p.key === key);
        setPendingParkers(prev => prev.filter(p => p.key !== key));
        if (pending?.isExtraSlot) {
            setExtraSpotIds(ids => [...ids, ...createExtraSpotIds(1)]);
        }
    };

    const openAssign = (placeholderKind?: PlaceholderKind, extraSpotId?: string) => {
        if (placeholderKind === "extra") {
            setAssignFillsExtraSlot(true);
            setAssignExtraSpotId(extraSpotId);
        } else if (placeholderKind === "base") {
            setAssignFillsExtraSlot(false);
            setAssignExtraSpotId(undefined);
        } else {
            setAssignFillsExtraSlot(basePlaceholderCount === 0);
            setAssignExtraSpotId(undefined);
        }
        setShowAssignModal(true);
    };

    const openAssignNextPlaceholder = (
        parkersOverride?: SubscriptionParker[],
        pendingCountOverride?: number,
        extraIdsOverride?: string[],
    ) => {
        const assigned = countAssignedSlots(
            parkersOverride ?? parkers,
            pendingCountOverride ?? pendingParkers.length,
            today,
        );
        const baseCount = Math.max(0, sub.quantity - assigned);
        const extras = extraIdsOverride ?? extraSpotIds;

        if (baseCount > 0) {
            openAssign("base");
            return;
        }
        if (extras.length > 0) {
            openAssign("extra", extras[0]);
        }
    };

    const handleFinalizeSuccess = async (
        newParkers: SubscriptionParker[],
        options?: { refresh?: boolean },
    ) => {
        let updatedParkers = parkers;
        if (options?.refresh && onRefreshParkers) {
            updatedParkers = await onRefreshParkers();
        } else if (newParkers.length > 0) {
            updatedParkers = [...parkers, ...newParkers];
        } else if (onRefreshParkers) {
            updatedParkers = await onRefreshParkers();
        }
        setParkers(updatedParkers);
        setPendingParkers([]);
        setShowFinalizeModal(false);
    };

    const handleAddSpots = (count: number) => {
        const capped = Math.min(count, remainingSpotCap);
        if (capped < 1) return;
        const newIds = createExtraSpotIds(capped);
        setExtraSpotIds(ids => [...ids, ...newIds]);
        setAssignAfterSpotsClose(newIds[0]);
    };

    useEffect(() => {
        if (showAddSpotsModal || !assignAfterSpotsClose) return;
        const extraSpotId = assignAfterSpotsClose;
        setAssignAfterSpotsClose(undefined);
        openAssign("extra", extraSpotId);
    }, [showAddSpotsModal, assignAfterSpotsClose]);

    const canAssign = totalPlaceholders > 0;
    const canRemoveBaseSlot = basePlaceholderCount > 0 && canManageParkers && canRemoveSlot;

    const handleRemoveExtraSlot = (extraSpotId: string) => {
        if (!canRemoveSlot) return;
        setExtraSpotIds(ids => ids.filter(id => id !== extraSpotId));
    };

    const handleAddParkerClick = () => {
        if (basePlaceholderCount > 0) {
            openAssign("base");
            return;
        }
        if (remainingSpotCap < 1) return;
        setShowAddSpotsModal(true);
    };

    const handleClearCart = useCallback(() => {
        setPendingParkers(prev => {
            const extraToRestore = prev.filter(p => p.isExtraSlot).length;
            if (extraToRestore > 0) {
                setExtraSpotIds(ids => [...ids, ...createExtraSpotIds(extraToRestore)]);
            }
            return [];
        });
    }, []);

    const { showModal: showLeaveCartModal, stay: stayOnPage, discard: discardCartAndLeave } =
        useUnsavedChangesGuard(!isNeedsSetup && pendingParkers.length > 0, handleClearCart);

    useEffect(() => {
        if (isNeedsSetup || pendingParkers.length === 0) return;
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [isNeedsSetup, pendingParkers.length]);

    useEffect(() => {
        if (isNeedsSetup || pendingParkers.length === 0) {
            setCartBanner(null);
            setPendingCheckout(false);
            return;
        }
        setPendingCheckout(true);
        setCartBanner({
            pendingCount: pendingParkers.length,
            dueToday: cartTotal,
            isAccountsReceivable: !!sub.isAccountsReceivable,
            canAssignMore: canAssign,
            onAssignAnother: () => openAssignNextPlaceholder(),
            onFinalize: () => setShowFinalizeModal(true),
            onClearCart: handleClearCart,
        });
    }, [isNeedsSetup, pendingParkers.length, cartTotal, canAssign, sub.isAccountsReceivable, setCartBanner, setPendingCheckout, handleClearCart]);

    useEffect(() => () => {
        setCartBanner(null);
        setPendingCheckout(false);
    }, [setCartBanner, setPendingCheckout]);

    return (
        <div ref={sectionRef} className="pb-3">
            <div className="gp-toolbar">
                <div className="gp-toolbar__top">
                    <InputGroup size="sm" className="gp-toolbar__search">
                        <InputGroup.Text className="border-end-0">
                            <Search size={12} className="text-muted" />
                        </InputGroup.Text>
                        <Form.Control
                            className="border-start-0"
                            placeholder="Search parkers…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </InputGroup>
                    <div className="gp-toolbar__actions">
                        {isEnhancedGroup && canManageParkers && (
                            <>
                                <Button
                                    size="sm"
                                    onClick={() => {
                                        setViewContractsInitialId(null);
                                        setShowViewContracts(true);
                                    }}
                                    className="gp-toolbar__btn-outline"
                                    title="View Contracts"
                                    disabled={groups.length === 0}
                                >
                                    <FileEarmarkText size={14} aria-hidden />
                                    <span className="gp-toolbar__btn-label">View Contracts</span>
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={() => setShowGroupModal(true)}
                                    className="gp-toolbar__btn-outline"
                                    title="Add Group"
                                >
                                    <People size={14} aria-hidden />
                                    <span className="gp-toolbar__btn-label">Add Group</span>
                                </Button>
                            </>
                        )}
                        <Button
                            size="sm"
                            onClick={handleAddParkerClick}
                            className="gp-toolbar__btn-main btn-main"
                            title="Add Parker(s)"
                        >
                            <PersonPlus size={14} aria-hidden />
                            <span className="gp-toolbar__btn-label">Add Parker(s)</span>
                        </Button>
                    </div>
                </div>

                <div className="gp-toolbar__filters">
                    <div className="gp-filter-block gp-filter-block--status">
                        <div className="gp-filter-block__label">Status</div>
                        <div className="gp-status-seg" role="group" aria-label="Status filter">
                            <button
                                type="button"
                                className={`gp-status-seg__btn${statusFilter === "all" ? " is-active" : ""}`}
                                onClick={() => setStatusFilter("all")}
                            >
                                All<span className="gp-status-seg__count">{statusCounts.all ?? 0}</span>
                            </button>
                            {presentStatuses
                                .filter(s => s !== "placeholder")
                                .map(s => (
                                    <button
                                        key={s}
                                        type="button"
                                        className={`gp-status-seg__btn${statusFilter === s ? " is-active" : ""}`}
                                        onClick={() => setStatusFilter(statusFilter === s ? "all" : s)}
                                    >
                                        {STATUS_LABELS[s]}
                                        <span className="gp-status-seg__count">{statusCounts[s] ?? 0}</span>
                                    </button>
                                ))}
                        </div>
                    </div>

                    {isEnhancedGroup && (
                        <>
                            <div className="gp-filter-divider" aria-hidden />
                            <div className="gp-filter-block gp-filter-block--groups">
                                <div className="gp-filter-block__label">Groups</div>
                                <div className="gp-group-chips" role="group" aria-label="Group filters">
                                    {groups.filter(g => g.id != null).map(g => {
                                        const selected = groupFilters.has(g.id!);
                                        return (
                                            <button
                                                key={g.id}
                                                type="button"
                                                className={`gp-group-chip${selected ? " is-active" : ""}`}
                                                onClick={() => toggleGroupFilter(g.id!)}
                                            >
                                                {g.name}
                                                <span className="gp-group-chip__count">{groupCounts[g.id!] ?? 0}</span>
                                            </button>
                                        );
                                    })}
                                    <button
                                        type="button"
                                        className={`gp-group-chip${groupFilters.has("unassigned") ? " is-active" : ""}`}
                                        onClick={() => toggleGroupFilter("unassigned")}
                                    >
                                        Unassigned
                                        <span className="gp-group-chip__count">{groupCounts.unassigned ?? 0}</span>
                                    </button>
                                </div>
                            </div>
                        </>
                    )}

                    <div className="gp-sort" ref={sortMenuRef}>
                        <div className="gp-sort__bar d-md-none">
                            <button
                                type="button"
                                className="gp-sort__trigger"
                                onClick={() => setShowSortMenu(v => !v)}
                                aria-expanded={showSortMenu}
                            >
                                <Filter size={14} aria-hidden />
                                Sort: {SORT_OPTIONS.find(o => o.key === sortKey)?.label ?? "Name"}
                            </button>
                            <button
                                type="button"
                                className="gp-sort__dir"
                                onClick={() => setSortAsc(v => !v)}
                                title={sortAsc ? "Ascending" : "Descending"}
                                aria-label={sortAsc ? "Sort ascending" : "Sort descending"}
                            >
                                {sortAsc ? <SortUp size={14} /> : <SortDown size={14} />}
                            </button>
                        </div>
                        <button
                            type="button"
                            className="gp-sort__trigger d-none d-md-inline-flex"
                            onClick={() => setShowSortMenu(v => !v)}
                            aria-expanded={showSortMenu}
                        >
                            <Filter size={14} aria-hidden />
                            Sort: {SORT_OPTIONS.find(o => o.key === sortKey)?.label ?? "Name"}
                        </button>
                        <button
                            type="button"
                            className="gp-sort__dir d-none d-md-inline-flex"
                            onClick={() => setSortAsc(v => !v)}
                            title={sortAsc ? "Ascending" : "Descending"}
                            aria-label={sortAsc ? "Sort ascending" : "Sort descending"}
                        >
                            {sortAsc ? <SortUp size={14} /> : <SortDown size={14} />}
                        </button>
                        {showSortMenu && (
                            <div className="gp-sort__menu" role="menu">
                                {SORT_OPTIONS.map(opt => (
                                    <button
                                        key={opt.key}
                                        type="button"
                                        role="menuitemradio"
                                        aria-checked={sortKey === opt.key}
                                        className={`gp-sort__option${sortKey === opt.key ? " is-active" : ""}`}
                                        onClick={() => {
                                            setSortKey(opt.key);
                                            setShowSortMenu(false);
                                        }}
                                    >
                                        <span>{opt.label}</span>
                                        {sortKey === opt.key && <Check2 size={16} aria-hidden />}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="gp-toolbar__meta">
                    <span>
                        Showing {filteredCards.length} of {allCards.length} parkers
                    </span>
                    {filtersActive && (
                        <button type="button" className="gp-toolbar__clear" onClick={clearFilters}>
                            Clear filters
                        </button>
                    )}
                </div>
            </div>

            {/* Parker cards — windowed against #content scroll (no nested scrollbox) */}
            {filteredCards.length === 0 ? (
                <div className="d-flex flex-column align-items-center text-center py-5 gap-3">
                    <div
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{ width: 64, height: 64, background: "rgba(13,110,253,0.1)" }}
                    >
                        <Search style={{ fontSize: 28, color: "#0d6efd" }} />
                    </div>
                    <div>
                        <div className="fw-semibold mb-1">No parkers match</div>
                        <div className="text-muted small">
                            Try a different search or clear filters to see everyone on this plan.
                        </div>
                    </div>
                    <Button size="sm" className="px-3" onClick={clearFilters}>
                        Clear filters
                    </Button>
                </div>
            ) : (
                <div ref={listRef} className="position-relative">
                    <div
                        style={{
                            height: listTotalSize,
                            width: "100%",
                            position: "relative",
                        }}
                    >
                        {rowVirtualizer.getVirtualItems().map(virtualRow => {
                            const card = filteredCards[virtualRow.index];
                            const isLast = virtualRow.index === filteredCards.length - 1;
                            return (
                                <div
                                    key={card.key}
                                    data-index={virtualRow.index}
                                    ref={node => {
                                        rowVirtualizer.measureElement(node);
                                        if (isLast) lastRowRef.current = node;
                                        else if (lastRowRef.current === node) lastRowRef.current = null;
                                    }}
                                    style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: "100%",
                                        transform: `translateY(${virtualRow.start - scrollMargin}px)`,
                                    }}
                                >
                                    {card.type === "parker" && card.sp ? (
                                        <ParkerCard
                                            card={card}
                                            sp={card.sp}
                                            planIsActive={canManageParkers}
                                            onManageParker={() => onManageParker?.(card.sp!)}
                                            onRemove={() => onRemoveParker?.(card.sp!)}
                                            onUndoCancel={() => onUndoCancelParker?.(card.sp!)}
                                            isEnhancedGroup={isEnhancedGroup}
                                            groups={groups}
                                            onAssignOrChangeGroup={() => setChangeGroupParker(card.sp)}
                                            onRemoveFromGroup={() => handleRemoveFromGroup(card.sp!)}
                                            onViewContract={(g) => {
                                                setViewContractsInitialId(g.id ?? null);
                                                setShowViewContracts(true);
                                            }}
                                        />
                                    ) : card.type === "pending" && card.pending ? (
                                        <PendingCard
                                            card={card}
                                            onRemove={() => handleRemovePending(card.key)}
                                            showGroupName={isEnhancedGroup}
                                        />
                                    ) : (
                                        <PlaceholderCard
                                            canRemove={
                                                canManageParkers && canRemoveSlot && (
                                                    (card.placeholderKind === "base" && canRemoveBaseSlot)
                                                    || (card.placeholderKind === "extra" && !!card.extraSpotId)
                                                )
                                            }
                                            placeholderKind={card.placeholderKind ?? "base"}
                                            onAssign={() => openAssign(card.placeholderKind ?? "base", card.extraSpotId)}
                                            onRemove={() => {
                                                if (card.placeholderKind === "extra" && card.extraSpotId) {
                                                    handleRemoveExtraSlot(card.extraSpotId);
                                                } else {
                                                    onRemoveBaseSlot?.();
                                                }
                                            }}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    {/* After list + marginTop:-40 so sticky bottom fade doesn't sit on first card */}
                    {showScrollHint && (
                        <div
                            className="pe-none"
                            style={{
                                position: "sticky",
                                bottom: 0,
                                height: 40,
                                marginTop: -40,
                                background: "linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1))",
                                zIndex: 1,
                            }}
                        />
                    )}
                </div>
            )}

            <GroupParkerAddSpotsModal
                show={showAddSpotsModal}
                onHide={() => setShowAddSpotsModal(false)}
                onConfirm={handleAddSpots}
                maxCount={remainingSpotCap}
            />

            <GroupParkerAddModal
                show={showAssignModal}
                onHide={() => setShowAssignModal(false)}
                facilityId={facilityId}
                subscriptionId={sub.subscriptionId}
                subscriptionStart={sub.start}
                pendingAssignCount={pendingParkers.length}
                pendingParkers={pendingParkers}
                fillsExtraSlot={assignFillsExtraSlot}
                canAssignAnother={canAssignAnother}
                isAccountsReceivable={sub.isAccountsReceivable}
                onAddToCart={handleAddToCart}
                skipBillingPreview={isNeedsSetup}
                isEnhancedGroup={isEnhancedGroup}
                groups={groupOptions}
                defaultSubscriptionGroupId={
                    (() => {
                        const ids = [...groupFilters].filter((k): k is number => typeof k === "number");
                        return ids.length === 1 ? ids[0] : undefined;
                    })()
                }
                basePriceCents={resolvePlanBasePriceCents(
                    sub.prices,
                    sub.planPriceId,
                    0,
                )}
            />

            {!isNeedsSetup && (
            <GroupParkerFinalizeModal
                show={showFinalizeModal}
                onHide={() => setShowFinalizeModal(false)}
                facilityId={facilityId}
                subscriptionId={sub.subscriptionId}
                isAccountsReceivable={sub.isAccountsReceivable}
                pending={pendingParkers}
                paymentMethodLabel={paymentMethodLabel}
                canChargeSubscription={canChargeSubscription}
                maxCreditCardAmountInCents={maxCreditCardAmountInCents}
                paymentMethodIsAch={paymentMethodIsAch}
                onSuccess={handleFinalizeSuccess}
            />
            )}

            <UnsavedChangesModal
                show={showLeaveCartModal}
                onStay={stayOnPage}
                onDiscard={discardCartAndLeave}
            />

            {isEnhancedGroup && changeGroupParker && (
                <ChangeParkerGroupModal
                    show
                    onClose={() => setChangeGroupParker(undefined)}
                    onConfirm={(groupId) => {
                        const sp = changeGroupParker;
                        setChangeGroupParker(undefined);
                        void handleChangeParkerGroup(sp, groupId);
                    }}
                    parkerName={changeGroupParker.parker.name || changeGroupParker.parker.emailAddress}
                    currentGroupId={changeGroupParker.subscriptionGroupId ?? null}
                    groups={groupOptions}
                />
            )}

            {isEnhancedGroup && (
                <GroupModal
                    show={showGroupModal}
                    onClose={() => setShowGroupModal(false)}
                    existingGroups={groups}
                    onSubmit={async (name, _groupId, options) => {
                        const created = await handleCreateGroup(name);
                        if (
                            created?.id != null &&
                            options?.addContractAfterSave
                        ) {
                            openAddContract(created.id);
                        }
                    }}
                />
            )}

            {isEnhancedGroup && (
                <ContractDetailsModal
                    show={showViewContracts}
                    onHide={() => setShowViewContracts(false)}
                    groups={groups}
                    initialGroupId={viewContractsInitialId}
                    basePriceCents={resolvePlanBasePriceCents(
                        sub.prices,
                        sub.planPriceId,
                        0,
                    )}
                    onAddContract={openAddContract}
                    onEditContract={openEditContract}
                />
            )}

            {isEnhancedGroup && showAddContract && editingGroupId != null && (
                <ContractRuleModal
                    show={showAddContract}
                    onClose={() => {
                        setShowAddContract(false);
                        setEditingGroupId(undefined);
                        setEditingRules([]);
                    }}
                    context={{ mode: "group", groupId: editingGroupId }}
                    groups={groups
                        .filter((g): g is ServerRequestsSubscriptionGroupsResponse & { id: number; name: string } =>
                            g.id != null && !!g.name)
                        .map((g) => ({ id: g.id, name: g.name }))}
                    basePriceCents={resolvePlanBasePriceCents(
                        sub.prices,
                        sub.planPriceId,
                        0,
                    )}
                    onSubmit={async (data) => {
                        await saveContract(data);
                    }}
                    isEditing={editingRules.length > 0}
                    initialRules={editingRules}
                    onRemoveContract={removeContract}
                />
            )}
        </div>
    );
}

// ── Parker card ────────────────────────────────────────────────────────────

interface ParkerCardProps {
    card:             ParkerCardData;
    sp:               SubscriptionParker;
    planIsActive:     boolean;
    onManageParker:   () => void;
    onRemove:         () => void;
    onUndoCancel?:    () => void;
    isEnhancedGroup?: boolean;
    groups?: ServerRequestsSubscriptionGroupsResponse[];
    onAssignOrChangeGroup?: () => void;
    onRemoveFromGroup?: () => void;
    onViewContract?: (group: ServerRequestsSubscriptionGroupsResponse) => void;
}

function ParkerCard({
    card,
    sp,
    planIsActive,
    onManageParker,
    onRemove,
    onUndoCancel,
    isEnhancedGroup = false,
    groups = [],
    onAssignOrChangeGroup,
    onRemoveFromGroup,
    onViewContract,
}: ParkerCardProps) {
    const actions: ActionItem[] = [];
    const assignedGroup = isEnhancedGroup && sp.subscriptionGroupId != null
        ? groups.find(g => g.id === sp.subscriptionGroupId)
        : undefined;
    const groupName = assignedGroup?.name
        || (sp.subscriptionGroupName || undefined);

    actions.push({
        label: "Manage Parker",
        icon: <PersonFill size={16} className="text-muted" />,
        onClick: onManageParker,
    });

    if (isEnhancedGroup && planIsActive) {
        actions.push({
            label: sp.subscriptionGroupId ? "Change Group" : "Assign to Group",
            icon: sp.subscriptionGroupId
                ? <Folder2Open size={16} className="text-muted" />
                : <FolderPlus size={16} className="text-muted" />,
            onClick: () => onAssignOrChangeGroup?.(),
        });
        if (sp.subscriptionGroupId) {
            actions.push({
                label: "Remove from Group",
                icon: <FolderMinus size={16} className="text-muted" />,
                onClick: () => onRemoveFromGroup?.(),
            });
        }
    }

    if (planIsActive && card.status === "canceling") {
        actions.push({
            label: "Undo Cancellation",
            icon: <ArrowCounterclockwise size={16} className="text-muted" />,
            onClick: () => onUndoCancel?.(),
        });
    }

    if (planIsActive && card.status !== "canceling") {
        actions.push({
            label: "Remove Parker",
            icon: <Trash size={16} className="text-muted" />,
            onClick: onRemove,
            variant: "danger",
        });
    }

    const badgeProps = statusBadgeProps(card.status);
    const startsOn = formatStartsOn(instantToLocalDate(sp.startDate ?? undefined), LocalDate.now());
    const cancelsOn = card.cancelDate ? `Cancels ${card.cancelDate}` : undefined;
    const metaParts = [startsOn, cancelsOn].filter(Boolean) as string[];
    const metaLine = metaParts.length > 0 ? metaParts.join(" · ") : undefined;
    const hasContract = !!(assignedGroup?.contractRules && assignedGroup.contractRules.length > 0);

    return (
        <div className="border rounded-3 px-3 py-2 bg-white d-flex align-items-center gap-2 gap-sm-3"
            style={{ minWidth: 0 }}>
            <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 fw-bold text-white"
                style={{ width: 32, height: 32, backgroundColor: avatarColor(card.label), fontSize: "0.75rem" }}>
                {getInitials(card.label)}
            </div>

            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div className="fw-semibold text-truncate">{card.label}</div>
                {card.email && card.email !== card.label && (
                    <div className="text-muted text-truncate" style={{ fontSize: "0.8rem", lineHeight: 1.2, marginTop: 1 }}>
                        {card.email}
                    </div>
                )}
                {metaLine && (
                    <div className="d-flex align-items-center gap-1 text-muted" style={{ fontSize: "0.8rem", lineHeight: 1.2, marginTop: 1 }}>
                        <Calendar3 size={12} className="flex-shrink-0" />
                        <span className="text-truncate">{metaLine}</span>
                    </div>
                )}
            </div>

            {groupName && (
                <Badge
                    pill
                    bg="light"
                    text="dark"
                    className={`flex-shrink-0 border fw-semibold${hasContract ? " cursor-pointer" : ""}`}
                    style={{
                        fontSize: "0.72rem",
                        ...(hasContract ? { cursor: "pointer" } : {}),
                    }}
                    title={hasContract ? "View contract" : undefined}
                    onClick={hasContract && assignedGroup
                        ? () => onViewContract?.(assignedGroup)
                        : undefined}
                    role={hasContract ? "button" : undefined}
                >
                    {groupName}
                </Badge>
            )}

            <Badge
                bg={badgeProps.bg}
                text={badgeProps.text}
                pill
                className={`flex-shrink-0 fw-semibold ${badgeProps.className}`}
                style={{ fontSize: "0.72rem" }}
            >
                {STATUS_LABELS[card.status]}
            </Badge>

            <ActionDropdown actions={actions} />
        </div>
    );
}

function PendingCard({
    card,
    onRemove,
    showGroupName = false,
}: {
    card: ParkerCardData;
    onRemove: () => void;
    showGroupName?: boolean;
}) {
    const actions: ActionItem[] = [
        {
            label: "Remove from cart",
            icon: <Trash size={16} className="text-muted" />,
            onClick: onRemove,
            variant: "danger",
        },
    ];

    const startsOn = formatStartsOn(
        card.pending ? LocalDate.parse(card.pending.startDate) : undefined,
        LocalDate.now(),
    );
    const groupName = showGroupName
        ? (card.pending?.subscriptionGroupName || undefined)
        : undefined;

    return (
        <div
            className="border rounded-3 px-3 py-2 bg-white d-flex align-items-center gap-2 gap-sm-3"
            style={{ background: "rgba(13,110,253,0.04)", borderColor: "rgba(13,110,253,0.25)", minWidth: 0 }}
        >
            <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 fw-bold text-white"
                style={{ width: 32, height: 32, backgroundColor: avatarColor(card.label), fontSize: "0.75rem" }}>
                {getInitials(card.label)}
            </div>
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div className="fw-semibold text-truncate">{card.label}</div>
                {card.email && (
                    <div className="text-muted text-truncate" style={{ fontSize: "0.8rem", lineHeight: 1.2, marginTop: 1 }}>
                        {card.email}
                    </div>
                )}
                <div className="d-flex align-items-center gap-1 text-muted flex-wrap" style={{ fontSize: "0.8rem", lineHeight: 1.2, marginTop: 1 }}>
                    {startsOn && (
                        <>
                            <Calendar3 size={12} className="flex-shrink-0" />
                            <span>{startsOn}</span>
                        </>
                    )}
                    <span>
                        {(card.previewSubtotal ?? card.previewPrice ?? 0) > 0
                            ? showMoney(cents(card.previewSubtotal ?? card.previewPrice ?? 0))
                            : "No charge today"}
                    </span>
                </div>
            </div>
            {groupName && (
                <Badge
                    pill
                    bg="light"
                    text="dark"
                    className="flex-shrink-0 border fw-semibold"
                    style={{ fontSize: "0.72rem" }}
                >
                    {groupName}
                </Badge>
            )}
            <Badge pill className="flex-shrink-0 fw-semibold bg-parker-pending" style={{ fontSize: "0.72rem" }}>
                Pending
            </Badge>
            <ActionDropdown actions={actions} />
        </div>
    );
}

function PlaceholderCard({
    canRemove,
    placeholderKind,
    onAssign,
    onRemove,
}: {
    canRemove: boolean;
    placeholderKind: PlaceholderKind;
    onAssign: () => void;
    onRemove: () => void;
}) {
    return (
        <div
            className="border rounded-3 px-3 py-2 d-flex align-items-center gap-2 gap-sm-3"
            style={{ borderStyle: "dashed", background: "#fafafa", minWidth: 0 }}
        >
            <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                style={{ width: 32, height: 32, background: "#e9ecef", color: "#adb5bd" }}>
                <PersonFill size={14} />
            </div>
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div className="text-muted fw-semibold" style={{ fontSize: "0.9rem" }}>Open Spot</div>
                <div className="text-muted small">Unassigned space</div>
            </div>
            <div className="d-flex align-items-center gap-2 flex-shrink-0">
                <BsButton
                    size="sm"
                    variant="outline-secondary"
                    className="d-flex align-items-center gap-1"
                    onClick={onAssign}
                >
                    <PencilFill size={12} />
                    <span className="d-none d-sm-inline">Assign Parker</span>
                </BsButton>
                {canRemove && (
                    <BsButton
                        size="sm"
                        variant="outline-danger"
                        className="d-flex align-items-center gap-1"
                        onClick={onRemove}
                        title={placeholderKind === "extra" ? "Remove Spot" : "Remove Open Spot"}
                    >
                        <Trash size={12} />
                        <span className="d-none d-sm-inline">Remove</span>
                    </BsButton>
                )}
            </div>
        </div>
    );
}
